import {
  IconBrandOffice,
  IconCalendarMonth,
  IconChecklist,
  IconDashboard,
  IconFileDescription,
  IconFileSpreadsheet,
  IconFridge,
  IconHistory,
  IconHome,
  IconMessages,
  IconMeteor,
  IconTool,
  IconTools,
  IconWallet,
} from '@tabler/icons-react';
import { TFunction } from 'i18next';
import { ExoticComponent } from 'react';

export interface MenuNeededPermission {
  subject: string;
  minLevel?: 'read' | 'own' | 'full';
}

export interface MenuLink {
  label: string;
  icon: ExoticComponent;
  type: 'link';
  getLinkWithAssetId?: (assetId: string) => string;
  getLink?: () => string;
  children?: MenuLink[];
  neededRole?: string[];
  forOwnerOnly?: boolean;
  neededPermission?: MenuNeededPermission;
}

interface MenuHeader {
  label: string;
  type: 'header';
  neededRole?: string[];
  forOwnerOnly?: boolean;
}

export interface MenuCollapsible {
  label: string;
  type: 'collapsible';
  storageKey: string;
  items: MenuLink[];
  neededRole?: string[];
  forOwnerOnly?: boolean;
}

export type MenuItem = MenuHeader | MenuLink | MenuCollapsible;

export const isMenuLink = (object: MenuItem): object is MenuLink => object.type === 'link';

export const isMenuHeader = (object: MenuItem): object is MenuHeader => object.type === 'header';

export const isMenuCollapsible = (object: MenuItem): object is MenuCollapsible =>
  object.type === 'collapsible';

export const config: (t: TFunction<never, undefined>) => MenuItem[] = t => [
  {
    label: t('menu.dashboard', 'Accueil'),
    icon: IconDashboard,
    type: 'link',
    getLinkWithAssetId: assetId => `/asset/${assetId}/dashboard`,
  },
  {
    label: t('menu.memories', 'Mémoire de la maison'),
    icon: IconHistory,
    type: 'link',
    getLinkWithAssetId: assetId => `/asset/${assetId}/memories`,
  },
  {
    label: t('menu.inventory', 'Inventaire'),
    icon: IconFridge,
    type: 'link',
    getLinkWithAssetId: assetId => `/asset/${assetId}/inventories`,
  },
  {
    label: t('menu.assetDescription', 'Description du bien'),
    icon: IconHome,
    type: 'link',
    neededPermission: { subject: 'cartography', minLevel: 'read' },
    getLinkWithAssetId: assetId => `/asset/${assetId}/admin?tab=structure`,
  },
  {
    label: t('menu.planning', 'Calendrier'),
    icon: IconCalendarMonth,
    type: 'link',
    getLinkWithAssetId: assetId => `/asset/${assetId}/planning`,
  },
  {
    label: t('menu.budget', 'Finances'),
    icon: IconWallet,
    type: 'link',
    getLinkWithAssetId: assetId => `/asset/${assetId}/expenses`,
  },
  {
    label: t('menu.documents', 'Documents'),
    icon: IconFileDescription,
    type: 'link',
    getLinkWithAssetId: assetId => `/asset/${assetId}/documents`,
  },
  {
    label: t('menu.projectsAndWork', 'Projets & Travaux'),
    icon: IconTools,
    type: 'link',
    getLinkWithAssetId: assetId => `/asset/${assetId}/project-and-works`,
  },
  {
    label: t('menu.infoAndContacts', 'Infos & Contacts'),
    icon: IconFileSpreadsheet,
    type: 'link',
    getLinkWithAssetId: assetId => `/asset/${assetId}/infos-et-contacts`,
  },
  {
    label: t('menu.checklistAndInstructions', "Mode d'emploi"),
    icon: IconChecklist,
    type: 'link',
    getLinkWithAssetId: assetId => `/asset/${assetId}/guides`,
  },
  {
    label: t('menu.community', 'Entre nous — le tchat'),
    icon: IconMessages,
    type: 'link',
    getLinkWithAssetId: assetId => `/asset/${assetId}/community`,
  },
  {
    label: t('menu.soonAvailable', 'Bientôt disponible'),
    type: 'collapsible',
    storageKey: 'soonAvailable',
    items: [
      {
        label: t('menu.meetings', 'Réunions & AG'),
        icon: IconBrandOffice,
        type: 'link',
        getLinkWithAssetId: assetId => `/asset/${assetId}/meetings`,
      },
      {
        label: t('menu.issues', 'Incidents & Sinistres'),
        icon: IconMeteor,
        type: 'link',
        getLinkWithAssetId: assetId => `/asset/${assetId}/incidents-and-claims`,
      },
      {
        label: t('menu.resourcesModule', 'Ressources & Équipements'),
        icon: IconTool,
        type: 'link',
        getLinkWithAssetId: assetId => `/asset/${assetId}/resources`,
      },
    ],
  },
];
